import { StackScreenProps } from "@react-navigation/stack";
import { ROAM_LOCATION_HEADER_KEY } from "../../constants";
import { QUERY_PRODUCT_LIST } from "../../modules/products/utils";
import { useLoadMoreList } from "../../utilities/load-more-list";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import {
  DEFAULT_PAGE_WIDTH,
  Text,
  FlatListSearchable,
  Page,
  useWithAppState,
  Divider,
  Button,
} from "@towersystems/roam-common-native";
import { useScheduler } from "../../utilities/scheduler";
import { useSearch } from "../../utilities/search";
import { ProductResourceListItem } from "../../modules/products/components/ProductResourceListItem";
import React from "react";
import { useSelectableProductsFilterContext } from "./hooks";
import { Spacings, View } from "react-native-ui-lib";
import { useTranslation } from "../../utilities/i18n";
import { FlatListSearchableSkeleton } from "../FlatListSearchableSkeleton";
import colors from "react-native-ui-lib/src/style/colors";
import { Feather } from "@expo/vector-icons";

export type SelectableProductsFilterScreenProps = StackScreenProps<
  any,
  "SelectableProductsFilterScreen"
>;

export const SelectableProductsFilterScreen = ({
  navigation,
}: SelectableProductsFilterScreenProps) => {
  const { t } = useTranslation("common");

  const { onProductSelect, selectedProductIds, onClearAllPress } =
    useSelectableProductsFilterContext();

  const {
    appState: { locationId },
  } = useWithAppState();

  const { schedule } = useScheduler(500);
  const search = useSearch();

  React.useEffect(() => {
    navigation.setOptions({
      title: "",
      headerRight() {
        return (
          <Button
            label={t("common:clear_filters")}
            isAction
            disabled={selectedProductIds.length == 0}
            onPress={onClearAllPress}
            size="compact"
          ></Button>
        );
      },
    });
  }, [selectedProductIds]);

  const loadMoreList = useLoadMoreList<Product>({
    initialLoad: false,
    query: QUERY_PRODUCT_LIST,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    context: {
      headers: {
        [ROAM_LOCATION_HEADER_KEY]: locationId ?? undefined,
      },
    },
    variableFormatter: (variables) => {
      return {
        options: variables,
      };
    },
  });

  React.useEffect(() => {
    if (search.touched) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query, search.touched]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
        },
      },
      reset
    );
  };

  const handleOnSelect = (product: Product) => {
    onProductSelect(product.id);
  };

  const renderItem = React.useCallback(
    ({ item }: { item: Product }) => {
      return (
        <ProductResourceListItem
          product={item}
          action={() => handleOnSelect(item)}
          componentRight={
            selectedProductIds.includes(item.id) ? (
              <Feather name="check" size={24} color={colors.primary} />
            ) : (
              <View />
            )
          }
        />
      );
    },
    [selectedProductIds]
  );

  const emptyComponentMarkup = (
    <View>
      <Text>{t("common:empty_list")}</Text>
    </View>
  );

  if (loadMoreList.loading && loadMoreList.called == false) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />;
      </Page>
    );
  }

  const items: Product[] = loadMoreList.items || [];

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder={t("search2")}
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        refreshing={loadMoreList.loading}
        data={items}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponentMarkup}
        onEndReached={() => handleLoadMore(false)}
        renderSearchBarHeader={(searchBarMarkup, divider) => (
          <View>
            <View>
              {searchBarMarkup}
              {divider}
            </View>
          </View>
        )}
      />
    </Page>
  );
};
